import { useEffect, useRef, useState } from 'react';
import { problems } from '@/mocks/homepage';

export default function ProblemSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="bg-cream-50 py-24 md:py-36">
      <div className="w-full px-6 md:px-10 lg:px-16">
        <div className="max-w-5xl mx-auto">
          {/* Section Header */}
          <div
            className={`text-center mb-16 md:mb-24 transition-all duration-1000 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <p className="text-sage-500 text-xs tracking-[0.3em] uppercase mb-4 font-medium">
              The Problem
            </p>
            <h2 className="font-serif text-3xl md:text-5xl text-charcoal-300 font-medium leading-tight max-w-3xl mx-auto">
              Modern life taught us how to earn.
              <br />
              <span className="italic font-light text-sage-400">Not how to steward.</span>
            </h2>
          </div>

          {/* Problem Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {problems.map((item, i) => (
              <div
                key={item.id}
                className={`group bg-white rounded-2xl p-8 md:p-10 transition-all duration-700 hover:bg-sage-50 ${
                  visible
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${200 + i * 150}ms` }}
              >
                <div className="flex items-start gap-4">
                  <span className="text-sage-300 font-serif text-xl md:text-2xl italic">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <h3 className="text-charcoal-300 text-lg md:text-xl font-medium mb-3">
                      {item.label}
                    </h3>
                    <p className="text-charcoal-100 text-sm md:text-base leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom reflection */}
          <div
            className={`mt-16 md:mt-24 text-center transition-all duration-1000 delay-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <p className="font-serif text-xl md:text-2xl text-charcoal-300 italic leading-relaxed max-w-2xl mx-auto">
              You are not bad with money.
              <br />
              You were just never taught to practice.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}